import { useContext } from 'react';

import {
  faCircleCheck,
  faCircleInfo,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
} from '@repo/videa-components/src/index';

import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import type { TrialStatus } from '../types';

interface Props {
  isCentralizedBilling?: boolean;
  trialLengthInDays?: number;
  trialStatus?: TrialStatus;
  onStartTrial: () => Promise<void>;
  isTrialLoading: boolean;
  isTrialError: boolean;
  isTrialSuccess: boolean;
}

export const TrialOfferDetails = ({
  isCentralizedBilling,
  trialLengthInDays,
  trialStatus,
  onStartTrial,
  isTrialLoading,
  isTrialError,
  isTrialSuccess,
}: Props) => {
  const externalPatientContext = useContext(ExternalPatientContext);
  const { trackEvent } = useTrackEvent();
  const trialDurationDays = trialLengthInDays ?? 30;

  const handleStartTrial = async () => {
    trackEvent(mixpanelEvents.externalTrialOfferClick, {
      ...externalPatientContext,
      trialStatus: trialStatus ?? null,
      centralizedBilling: isCentralizedBilling ?? null,
      isIframe: true,
    });
    await onStartTrial();
  };

  return (
    <div className="vai-flex vai-flex-col vai-gap-3 vai-flex-1 vai-text-white">
      <div className="vai-flex vai-flex-col vai-gap-2 vai-flex-1">
        <p className="vai-font-sans vai-font-semibold vai-text-lg vai-leading-6 vai-mt-2 vai-mb-3">
          <Trans>Access AI clinical support now!</Trans>
        </p>
        <img
          alt="AI findings preview"
          className="vai-rounded-lg vai-w-full vai-object-contain"
          src="assets/images/external/image-with-findings-v2.png"
        />
        <div className="vai-flex vai-flex-col vai-space-y-3">
          <p className="vai-font-inter vai-text-sm">
            <Trans>
              AI insights are available to your clinical team with one click
              below. Try enhanced visualizations and patient education tools to
              advance your practice for{' '}
              <span className="vai-font-bold">{trialDurationDays} days</span>.
            </Trans>
          </p>
          <p className="vai-font-inter vai-text-sm">
            <Trans>
              You will <span className="vai-font-bold">NOT</span> be billed
              after your trial period ends unless you choose to purchase a
              Detect AI subscription.
            </Trans>
          </p>
        </div>

        {isTrialSuccess ? (
          <Alert variant="successSolid">
            <FontAwesomeIcon icon={faCircleCheck} />
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>
              <Trans>
                Your trial has been started. You will see AI on the next patient
                you open.
              </Trans>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Button
              className="vai-mt-4"
              disabled={isTrialLoading}
              onClick={handleStartTrial}
            >
              {isTrialLoading && (
                <div className="vai-animate-spin vai-rounded-full vai-h-4 vai-w-4 vai-border-2 vai-border-t-transparent" />
              )}
              <Trans>Start Now</Trans>
            </Button>
            {isTrialError && (
              <Alert variant="destructiveSolid">
                <FontAwesomeIcon icon={faCircleInfo} />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  <Trans>
                    Something went wrong when starting your trial. Please try
                    again.
                  </Trans>
                </AlertDescription>
              </Alert>
            )}
            <p className="vai-font-inter vai-text-[10px] vai-text-slate-400">
              <Trans>
                By accepting this evaluation product, you agree to these{' '}
                <a
                  className="vai-text-blue-500 vai-underline"
                  href="https://hsps.widen.net/s/rj8hgnltws/eservices---hs1-terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    trackEvent(mixpanelEvents.externalTermsAndConditionsClick, {
                      ...externalPatientContext,
                      source: 'TRIAL_OFFER',
                    });
                  }}
                >
                  terms and conditions
                </a>{' '}
                and that you will not charge, or be reimbursed by any federal
                program (e.g., Medicare, Medicaid, Tricare of Children&apos;s
                Health Insurance Program), or any third party for such
                evaluation product or its use. The evaluation trial period will
                last for {trialDurationDays} days and will automatically cease
                at the end of such period.
              </Trans>
            </p>
          </>
        )}
      </div>
    </div>
  );
};
